// The ONE definition of "this tool call embedded a live block in the thread"
// (DESIGN-WL-ASSISTANT-MAIN-VIEW-1) — used by BOTH halves of the thread, like
// toolOutcome.js:
//   live turn    — the `tool.activity` completed frame of embed_records /
//                  embed_widget carries `embed` (simoneAiTransport
//                  _patchToolActivity attaches the card)
//   history load — the persisted tool_result payload carries `output.embed`
//                  (sessions._toChatMessages restores the same card)
// The server shapes (ai-boards assistantTools.js):
//   { kind: 'records', block, size? }  a RECORDS BLOCK v1, resolved per viewer
//   { kind: 'widget', widget, params?, size?, title?, pinKey? }  an installed
//                                      app's widget with its params
//   { kind: 'view', view, params?, size?, title? }  an app's read-only VIEW
//                                      (manifest views[] / a built-in package's
//                                      provider) — drawn as a widget card
//   { kind: 'sources', sources: [{ title, url, snippet }], text?, query? }
//                                      web results / a fetched page (U4) —
//                                      source cards or a quote with its source
//   { kind: 'form', form, values, recordId?, title? }  an app's own FORM,
//                                      prefilled from the proposal — a form
//                                      card (functions/assistantAppForms.js)
// → the thread card: { type: 'records', block, size? } (the records path the
// thread already draws: ChatCard → AssistantThreadBlock → AssistantRecordsBlock)
// or { type: 'widget', widget, params, size?, title?, pinKey? }
// (AssistantThreadBlock's widget body). No embed, a refused call, an unknown
// kind or a malformed payload → null: the thread adds nothing.
// Every card carries a TITLE, so every host shows one (a host without the
// shell's block component reads the card through ChatCard's PvCardFallback,
// which shows `title` — without one it was an empty card, design-d2
// 2026-09-29): the model's own, else a readable default — the widget id's last
// part ("crm.crm-pipeline" → "Crm pipeline"), the block's title text, or its
// collection — marked `titleDefault`, so the shell still prefers the
// installed widget's own title / the records block's own (bundle-keyed) title.
export const toolEmbedCard = (x) => {
  const plain = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null)
  const readable = (ref) => {
    const tail = String(ref || '')
      .split('.')
      .pop()
      .replace(/[-_]+/g, ' ')
      .trim()
    return tail ? tail.charAt(0).toUpperCase() + tail.slice(1) : ''
  }
  const src = plain(x)
  if (!src || src.ok === false) return null
  const out = plain(src.output)
  if (out && out.ok === false) return null
  const e = plain(src.embed) || (out && plain(out.embed))
  if (!e) return null
  const size = ['s', 'm', 'l', 'xl'].includes(e.size) ? e.size : null
  if (e.kind === 'records') {
    const block = plain(e.block)
    if (!block) return null
    const own = plain(block.title)
    const title =
      (own && typeof own.text === 'string' && own.text.trim()) || readable(block.collection)
    return {
      type: 'records',
      block,
      ...(size ? { size } : {}),
      ...(title ? { title, titleDefault: true } : {})
    }
  }
  // A VIEW (an app's read-only view, functions/assistantAppViews.js) is drawn
  // by the widget card: the same row, mount and Open in split.
  if (e.kind === 'widget' || e.kind === 'view') {
    const ref = e.kind === 'view' ? e.view : e.widget
    if (typeof ref !== 'string' || !ref) return null
    const own = typeof e.title === 'string' && e.title.trim() ? e.title.trim() : ''
    const title = own || readable(ref)
    return {
      type: 'widget',
      widget: ref,
      params: plain(e.params) || {},
      ...(size ? { size } : {}),
      ...(title ? { title } : {}),
      ...(!own && title ? { titleDefault: true } : {}),
      ...(typeof e.pinKey === 'string' && e.pinKey ? { pinKey: e.pinKey } : {})
    }
  }
  // WEB SOURCES (web_search / fetch_url, U4): search results become source
  // cards; a fetched page's `text` a quote with its source. Only http(s)
  // links survive (never a javascript: or data: URL); at most 8 sources;
  // title 200, snippet 400, text 1200 characters. No usable source, no card.
  if (e.kind === 'sources') {
    const list = Array.isArray(e.sources) ? e.sources : []
    const sources = []
    for (const it of list) {
      const item = plain(it)
      if (!item || typeof item.url !== 'string' || !/^https?:\/\/[^\s]+$/i.test(item.url)) continue
      sources.push({
        title: typeof item.title === 'string' ? item.title.trim().slice(0, 200) : '',
        url: item.url,
        snippet: typeof item.snippet === 'string' ? item.snippet.trim().slice(0, 400) : ''
      })
      if (sources.length === 8) break
    }
    if (!sources.length) return null
    const text = typeof e.text === 'string' ? e.text.trim().slice(0, 1200) : ''
    const query = typeof e.query === 'string' ? e.query.trim().slice(0, 200) : ''
    return {
      type: 'sources',
      sources,
      ...(text ? { text } : {}),
      ...(query ? { query } : {})
    }
  }
  // A FORM (an app's own form, prefilled from the assistant's proposal —
  // functions/assistantAppForms.js): nothing is written until the member saves
  // in the app's form.
  if (e.kind === 'form') {
    if (typeof e.form !== 'string' || !e.form) return null
    const own = typeof e.title === 'string' && e.title.trim() ? e.title.trim() : ''
    const title = own || readable(e.form)
    return {
      type: 'form',
      form: e.form,
      values: plain(e.values) || {},
      ...(typeof e.recordId === 'string' && e.recordId ? { recordId: e.recordId } : {}),
      ...(title ? { title } : {}),
      ...(!own && title ? { titleDefault: true } : {})
    }
  }
  return null
}

// A FLY PAGE kept on its thread's answer (the server's `metadata.flyPage`, Fly
// mode — docs/FLY_MODE.md): its sections become the answer's live cards, the
// same cards an embed tool draws — a widget or an app view as a widget card,
// a records section as a records block (read under this viewer's grants) —
// and its word blocks become the answer's words. Nika 2026-10-18: a Fly thread
// opened as a thread showed the openings only, nothing interactive.
// A UI SECTION (Fly train 2: one read's result, the chat's answer-UI payload,
// kept on the page as it was shown) becomes the chat's OWN cards again:
// render_cards' cards as they are (the history path restores a render_cards
// call the same way, sessions.js), a render_answer_ui view as a VIEW card that
// carries its source — the thread registers it again when it mounts
// (assistantThreadBlocks.js assistantBlockMountView). A section whose read
// found nothing, or that did not come back, adds nothing.
// → { cards, words }; a malformed page gives neither.
export const flyPageEmbeds = (page) => {
  const plain = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null)
  const p = plain(page)
  const items = p && Array.isArray(p.items) ? p.items : []
  const cards = []
  const words = []
  for (const raw of items) {
    const it = plain(raw)
    if (!it) continue
    const head = plain(it.title) ? it.title.text : it.title
    const title = typeof head === 'string' && head.trim() ? head.trim() : ''
    let card = null
    if (it.kind === 'records') {
      card = toolEmbedCard({ embed: { kind: 'records', block: it, size: it.size } })
    } else if (it.kind === 'widget' && typeof it.widget === 'string') {
      card = toolEmbedCard({
        embed: {
          kind: 'widget',
          widget: it.widget,
          params: it.params,
          size: it.size,
          ...(title ? { title } : {})
        }
      })
    } else if (it.kind === 'ui') {
      const ui = plain(it.ui)
      const args = ui && plain(ui.args)
      if (ui && args && ui.tool === 'render_cards' && Array.isArray(args.cards)) {
        for (const c of args.cards) {
          const one = plain(c)
          if (one && typeof one.type === 'string' && Array.isArray(one.items) && one.items.length) {
            cards.push({ type: one.type, items: one.items })
          }
        }
      } else if (
        ui &&
        args &&
        ui.tool === 'render_answer_ui' &&
        typeof args.source === 'string' &&
        args.source.trim()
      ) {
        const own = typeof args.title === 'string' ? args.title.trim() : ''
        card = {
          type: 'view',
          title: title || own,
          source: args.source,
          ...(args.placement === 'split' ? { placement: 'split' } : {})
        }
      }
    } else if (it.kind === 'block') {
      const d = plain(it.data) || {}
      const lines = title ? [title] : []
      if (typeof d.text === 'string' && d.text.trim()) lines.push(d.text.trim())
      for (const r of Array.isArray(d.rows) ? d.rows : []) {
        const row = plain(r)
        const t = row && (row.title || row.text)
        if (typeof t === 'string' && t.trim()) {
          lines.push(
            '- ' + t.trim() + (typeof row.meta === 'string' && row.meta ? ' · ' + row.meta : '')
          )
        }
      }
      const chips = (Array.isArray(d.chips) ? d.chips : []).filter(
        (c) => typeof c === 'string' && c.trim()
      )
      if (chips.length) lines.push(chips.join(' · '))
      if (lines.length > (title ? 1 : 0)) words.push(lines.join('\n'))
    }
    if (card) cards.push(card)
  }
  return { cards, words: words.join('\n\n') }
}
